import React, { useState, useMemo, Dispatch, SetStateAction } from 'react'
import { useNavigate } from 'react-router-dom'
import { t } from '@lingui/core/macro'
import { payPix } from '../../api/payPix'
import { useFormValidation, ValidationRules } from '../../hooks/useFormValidation'
import { extraFieldShowFilter } from '../../utils/filters'
import { paymentErrorHandler } from '../../utils/paymentErrorHandler'
import { PaymentMethodType, PaymentPageInfo } from '../../types/paymentPageInfo'
import { getPaymentMethodExtraField } from '../../types/paymentMethod'
import Money from '../../types/Money'
import ExtraFields from '../ExtraFields/ExtraFields'
import SubmitButton from '../SubmitButton/SubmitButton'
import InputForm from '../InputForm/InputForm'
import styles from './PaymentMethod.module.css'

interface PixPaymentProps {
    paymentPageInfo: PaymentPageInfo
    paymentMethod: PaymentMethodType
    money?: Money
    email: string
    setParentError: (field: string, message: string) => void
    validateParentFields: (fields: Record<string, string>) => boolean
    setPaymentError: Dispatch<SetStateAction<boolean>>
}

const PixPaymentMethod = ({
    paymentPageInfo,
    paymentMethod,
    money,
    email,
    setParentError,
    validateParentFields,
    setPaymentError,
}: PixPaymentProps) => {
    const navigate = useNavigate()

    const [pixData, setPixData] = useState({
        cpf: '',
        name: '',
    })

    const [extraFieldsData, setExtraFieldsData] = useState<Record<string, string>>(() => {
        const initialData: Record<string, string> = {}

        paymentMethod.extraFields.forEach(field => {
            initialData[field.name] = field.value || ''
        })

        return initialData
    })

    const [isSubmitting, setIsSubmitting] = useState(false)

    const validationRules = useMemo(() => {
        const rules: ValidationRules = {
            cpf: {
                required: true,
                validation: getPaymentMethodExtraField('cpf')?.validation,
            },
            name: {
                required: true,
                validation: (name: string): string | null => {
                    const cleanName = name.trim()

                    if (cleanName.length === 0) {
                        return t`Имя не может быть пустым`
                    }

                    if (cleanName.length < 2 || cleanName.length > 50) {
                        return t`Имя должно содержать от 2 до 50 символов`
                    }

                    return null
                },
            },
        }

        paymentMethod.extraFields
            .filter(extraFieldShowFilter)
            .forEach(field => {
                rules[field.name] = {
                    required: true,
                    validation: getPaymentMethodExtraField(field.name)?.validation,
                }
            })

        return rules
    }, [paymentMethod.extraFields])

    const { errors, setError, removeError, validateAllFields } = useFormValidation(validationRules)

    const handleCardDataChange = (name: string, value: string) => {
        setPixData(prev => ({ ...prev, [name]: value }))
        removeError(name)
    }

    const handleExtraFieldChange = (name: string, value: string) => {
        setExtraFieldsData(prev => ({ ...prev, [name]: value }))
        removeError(name)
    }

    const formatCPF = (value: string) => {
        return value.replace(/[^0-9]/gi, '')
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        const validParentFields = validateParentFields({ email: email, amount: money?.amount || '' })
        const validThisFields = validateAllFields({ ...pixData, ...extraFieldsData })

        if (!validParentFields || !validThisFields) {
            return
        }

        setIsSubmitting(true)

        try {
            const transaction = await payPix(paymentPageInfo.id, pixData, { email: email, ...extraFieldsData }, money)

            navigate(`/payment/${paymentPageInfo.id}/pix/${transaction.transactionId}`)
        } catch (error) {
            paymentErrorHandler(error, setError, setParentError, setPaymentError)
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <div className={styles.container}>
            <h2 className={styles.title}>{t`Платежные данные`}</h2>

            <form onSubmit={handleSubmit} className={styles.form}>
                <div className={styles.cardSection}>
                    <InputForm
                        label="CPF *"
                        type="text"
                        name="cpf"
                        value={pixData.cpf}
                        placeholder="12345678909"
                        onChange={(e) => handleCardDataChange('cpf', formatCPF(e.target.value))}
                        maxLength={11}
                        errorMessage={errors.cpf || undefined}
                    />

                    <InputForm
                        label={t`Имя *`}
                        type="text"
                        name="name"
                        value={pixData.name}
                        placeholder="Francisco da Silva"
                        onChange={(e) => handleCardDataChange('name', e.target.value)}
                        maxLength={50}
                        errorMessage={errors.name || undefined}
                    />
                </div>

                {paymentMethod.extraFields.length > 0 && (
                    <ExtraFields
                        extraFields={paymentMethod.extraFields}
                        data={extraFieldsData}
                        errors={errors}
                        handleExtraFieldChange={handleExtraFieldChange}
                    />
                )}

                <SubmitButton isSubmitting={isSubmitting} />
            </form>
        </div>
    )
}

export default PixPaymentMethod
